import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Wrench, Plus, X, Edit, Trash2, Save, Phone } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";
import { usePermissions } from "../lib/permissions";

const empty = { name: "", trade: "", phone: "", email: "" };

export default function MaintenanceContacts({ testid = "maintenance-contacts" }) {
  const { canEdit, canDelete } = usePermissions();
  const manage = canEdit; // admin/manager
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/maintenance-contacts");
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const startAdd = () => {
    setEditingId(null);
    setForm(empty);
    setShowAdd(true);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, trade: c.trade, phone: c.phone, email: c.email || "" });
    setShowAdd(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.trade.trim() || !form.phone.trim()) {
      setError("Name, trade and phone are all required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        trade: form.trade.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
      };
      if (editingId) {
        await api.patch(`/maintenance-contacts/${editingId}`, payload);
      } else {
        await api.post("/maintenance-contacts", payload);
      }
      setShowAdd(false);
      setForm(empty);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/maintenance-contacts/${confirmDel.id}`);
      setConfirmDel(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
      setConfirmDel(null);
    }
  };

  return (
    <div className="se-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <div className="se-label flex items-center gap-2"><Wrench size={12} /> Maintenance</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 18 }}>Contacts</h3>
          <p className="text-xs mt-1" style={{ color: "#8C9E8A" }}>
            Trusted electricians, plumbers, farriers, vets and other on-call services.
          </p>
        </div>
        {manage && !showAdd && (
          <button className="se-btn" onClick={startAdd} data-testid={`${testid}-add-button`}>
            <Plus size={14} /> Add contact
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid={`${testid}-error`}>
          {error}
        </div>
      )}

      {showAdd && (
        <form onSubmit={save} className="mb-4 p-3 rounded-lg" style={{ background: "rgba(212,169,106,0.08)" }} data-testid={`${testid}-form`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input className="se-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid={`${testid}-form-name`} />
            <input className="se-input" placeholder="Trade (electrician, farrier…)" value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} required data-testid={`${testid}-form-trade`} />
            <input className="se-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid={`${testid}-form-phone`} />
            <input className="se-input" type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${testid}-form-email`} />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="se-btn se-btn-ghost" onClick={() => { setShowAdd(false); setEditingId(null); setForm(empty); }} data-testid={`${testid}-form-cancel`}>
              <X size={14} /> Cancel
            </button>
            <button type="submit" className="se-btn" disabled={busy} data-testid={`${testid}-form-submit`}>
              <Save size={14} /> {busy ? "Saving…" : (editingId ? "Save" : "Add contact")}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2" data-testid={`${testid}-list`}>
        {items === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
        {items !== null && items.length === 0 && !showAdd && (
          <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid={`${testid}-empty`}>
            No contacts added yet.
          </div>
        )}
        {(items || []).map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-3 p-3 rounded-lg"
            style={{ background: "rgba(212,169,106,0.06)", border: "0.5px solid rgba(212,169,106,0.2)" }}
            data-testid={`${testid}-item-${c.id}`}
          >
            <div className="flex-1 min-w-0">
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>{c.trade}</div>
              {c.email && (
                <a href={`mailto:${c.email}`} className="se-link text-xs" data-testid={`${testid}-email-${c.id}`}>
                  {c.email}
                </a>
              )}
            </div>
            <a
              href={`tel:${c.phone}`}
              className="se-link inline-flex items-center gap-1 text-sm"
              data-testid={`${testid}-phone-${c.id}`}
            >
              <Phone size={12} /> {c.phone}
            </a>
            {manage && (
              <div className="flex gap-1">
                <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => startEdit(c)} data-testid={`${testid}-edit-${c.id}`} aria-label="Edit contact">
                  <Edit size={12} />
                </button>
                {canDelete && (
                  <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => setConfirmDel(c)} data-testid={`${testid}-delete-${c.id}`} aria-label="Delete contact">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={remove}
        title={`Delete contact "${confirmDel?.name}"?`}
        description="This contact will be removed from your maintenance list."
        confirmLabel="Yes, delete"
        testid={`${testid}-confirm`}
      />
    </div>
  );
}
